import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import GameDetail from "./pages/GameDetail";
import DeveloperProfile from "./pages/DeveloperProfile";
import KeyRequests from "./pages/KeyRequests";
import Settings from "./pages/Settings";
import Games from "./pages/Games";
import Help from "./pages/Help";
import Messages from "./pages/Messages";
import NotFound from "./pages/NotFound";
import UsersList from "./pages/UsersList";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Subscriptions from "./pages/Subscriptions";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProducts from "./pages/AdminProducts";
import AdminMembers from "./pages/AdminMembers";
import AdminMemberDetail from "./pages/AdminMemberDetail";
import AdminGames from "./pages/AdminGames";
import AdminBanners from "./pages/AdminBanners";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import Coverage from "./pages/Coverage";
import Missions from "./pages/Missions";
import PlayerProfile from "./pages/PlayerProfile";
import PublicPlayerProfile from "./pages/PublicPlayerProfile";
import CreatorProfile from "./pages/CreatorProfile";
import Ranking from "./pages/Ranking";
import Calendar from "./pages/Calendar";
import Contact from "./pages/Contact";
import DeveloperStats from "./pages/DeveloperStats";
import PressKit from "./pages/PressKit";
import OAuthCallback from "./pages/OAuthCallback";
import MollieCallback from "./pages/MollieCallback";
import MySubscriptions from "./pages/MySubscriptions";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import BetaIdeas from "./pages/BetaIdeas";
import AdminBetaIdeas from "./pages/AdminBetaIdeas";
import Banned from "./pages/Banned";
import AdminKeyVerification from "./pages/AdminKeyVerification";
import AdminEvents from "./pages/AdminEvents";
import EventPage from "./pages/EventPage";
import Shop from "./pages/Shop";
import Inventory from "./pages/Inventory";


const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/banned" element={<Banned />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/help" element={<Help />} />
            <Route path="/payment/mollie/callback" element={<MollieCallback />} />
            <Route path="/auth/:platform/callback" element={<OAuthCallback />} />
            <Route path="/p/:playerName" element={<PublicPlayerProfile />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/presskit/:gameId" element={<PressKit />} />

            {/* Protected routes - authentication required */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/game/:gameId" element={<ProtectedRoute><GameDetail /></ProtectedRoute>} />
            <Route path="/developer/:identifier" element={<ProtectedRoute><DeveloperProfile /></ProtectedRoute>} />
            <Route path="/key-requests" element={<ProtectedRoute><KeyRequests /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/games" element={<ProtectedRoute><Games /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/coverage" element={<ProtectedRoute><Coverage /></ProtectedRoute>} />
            <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
            <Route path="/player-profile" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
            <Route path="/creator-profile" element={<ProtectedRoute><CreatorProfile /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
            <Route path="/statistics" element={<ProtectedRoute><DeveloperStats /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UsersList /></ProtectedRoute>} />
            <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
            <Route path="/my-subscriptions" element={<ProtectedRoute><MySubscriptions /></ProtectedRoute>} />
            <Route path="/beta-ideas" element={<ProtectedRoute><BetaIdeas /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/members" element={<ProtectedRoute><AdminMembers /></ProtectedRoute>} />
            <Route path="/admin/members/:memberId" element={<ProtectedRoute><AdminMemberDetail /></ProtectedRoute>} />
            <Route path="/admin/games" element={<ProtectedRoute><AdminGames /></ProtectedRoute>} />
            <Route path="/admin/banners" element={<ProtectedRoute><AdminBanners /></ProtectedRoute>} />
            <Route path="/admin/subscriptions" element={<ProtectedRoute><AdminSubscriptions /></ProtectedRoute>} />
            <Route path="/admin/beta-ideas" element={<ProtectedRoute><AdminBetaIdeas /></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
            <Route path="/admin/verify-keys/:token" element={<AdminKeyVerification />} />
            <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />


            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            {/* Dynamic event pages - must be last before 404 */}
            <Route path="/:eventSlug" element={<ProtectedRoute><EventPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
